// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState } from 'react';
import Modal from './Modal';
import TemporalAlertCascade from './TemporalAlertCascade';
import './AlertHistoryModal.css';

const AlertHistoryModal = ({ fullAlertHistory, onClose, onOpenSystemStatus, isOpen = true }) => {
  const [filter, setFilter] = useState(null); // null = show all, or 'CRITICAL', 'WARNING', 'OPTIMAL'
  
  // Filter alerts based on selected filter
  const filteredAlerts = filter 
    ? fullAlertHistory.filter(alert => {
        if (filter === 'WARNING') {
          return alert.severity === 'WARNING' || alert.severity === 'CAUTION';
        }
        return alert.severity === filter;
      })
    : fullAlertHistory;
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="◊ SYSTEM ALERT HISTORY ◊"
      size="large"
      className="alert-history-modal"
      footer={
        <button className="alert-history-close" onClick={onClose}>
          CLOSE
        </button>
      }
    >
      {/* Main Content Area */}
      <div className="alert-history-content">
          {/* Left Side - Helix Visualization */}
          <div className="helix-section">
            {/* Stats Panel */}
            <div className="stats-panel">
              <div 
                className={`stat-item ${filter === 'CRITICAL' ? 'stat-active' : ''}`}
                onClick={() => setFilter(filter === 'CRITICAL' ? null : 'CRITICAL')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value critical-stat">
                  {fullAlertHistory.filter(a => a.severity === 'CRITICAL').length}
                </div>
                <div className="stat-label">CRITICAL</div>
              </div>
              <div 
                className={`stat-item ${filter === 'WARNING' ? 'stat-active' : ''}`}
                onClick={() => setFilter(filter === 'WARNING' ? null : 'WARNING')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value warning-stat">
                  {fullAlertHistory.filter(a => a.severity === 'WARNING' || a.severity === 'CAUTION').length}
                </div>
                <div className="stat-label">WARNING</div>
              </div>
              <div 
                className={`stat-item ${filter === 'OPTIMAL' ? 'stat-active' : ''}`}
                onClick={() => setFilter(filter === 'OPTIMAL' ? null : 'OPTIMAL')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value optimal-stat">
                  {fullAlertHistory.filter(a => a.severity === 'OPTIMAL').length}
                </div>
                <div className="stat-label">OPTIMAL</div>
              </div>
              <div 
                className="stat-item"
                onClick={() => setFilter(null)}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value total-stat">
                  {fullAlertHistory.length}
                </div>
                <div className="stat-label">TOTAL</div>
              </div>
            </div>
            
            {/* Cascade Visualization */}
            <div className="helix-container">
              {/* Grid Background */}
              <div className="helix-grid" />
              
              {/* Title */}
              <div className="helix-title">
                TEMPORAL ALERT MATRIX
              </div>
              
              {/* Temporal Alert Cascade */}
              <TemporalAlertCascade alerts={fullAlertHistory} />
              
              {/* Rotating rings */}
              <div className="rotating-ring ring-1" />
              <div className="rotating-ring ring-2" />
            </div>
          </div>
          
          {/* Right Side - Alert List */}
          <div className="alerts-section">
            {/* Alert Count */}
            <div className="alerts-info">
              {filter ? (
                <>
                  Showing {filteredAlerts.length} {filter.toLowerCase()} alerts • 
                  <span 
                    onClick={() => setFilter(null)} 
                    style={{ 
                      color: '#00ffff', 
                      cursor: 'pointer', 
                      textDecoration: 'underline',
                      marginLeft: '5px'
                    }}
                  >
                    Clear filter
                  </span>
                </>
              ) : (
                `Showing ${fullAlertHistory.length} most recent alerts • Click any alert to view system status`
              )}
            </div>
            
            {/* Alert List */}
            <div className="alerts-list">
              {filteredAlerts.length > 0 ? (
                filteredAlerts.map((alert, index) => (
                  <div
                    key={alert.id}
                    className={`alert-history-item ${alert.severity.toLowerCase()}`}
                    onClick={onOpenSystemStatus}
                    style={{ '--item-index': index }}
                  >
                    {/* Alert Number */}
                    <div className="alert-number">
                      #{index + 1}
                    </div>
                    
                    {/* Icon */}
                    <div className="alert-history-icon">
                      {alert.severity === 'CRITICAL' ? '⚠' :
                       alert.severity === 'WARNING' ? '⚠' :
                       alert.severity === 'CAUTION' ? '🔧' : '✓'}
                    </div>
                    
                    {/* Content */}
                    <div className="alert-history-content-text">
                      <div className="alert-history-message">
                        {alert.message}
                      </div>
                      <div className="alert-history-time">
                        {alert.timestamp}
                      </div>
                    </div>
                    
                    {/* Severity Badge */}
                    <div className="alert-severity-badge">
                      {alert.severity}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-alerts-history">
                  <div className="no-alerts-icon">📋</div>
                  <div>No alerts in history</div>
                </div>
              )}
            </div>
          </div>
        </div>
    </Modal>
  );
};

export default AlertHistoryModal;
